import React from 'react';
import { Button, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PvpMatchPlayerRef, PvpMatchRow } from '@/types/pvp';
import {
  fmtDuration,
  fmtNum,
  payoutLabel,
  playerLabel,
  reasonLabel,
  resultLabel,
  scoreLabel,
  whenLabel,
} from './model';

const { Text } = Typography;

const STATUS_COLOR = { live: 'blue', ended: 'green', aborted: 'red' } as const;

interface NameProps {
  player: PvpMatchPlayerRef | null | undefined;
  onOpenPlayer: (userId: string) => void;
}

/** A player's name; a real player's opens the player drawer, a bot's is plain with a "bot" tag. */
export const PlayerName: React.FC<NameProps> = ({ player, onOpenPlayer }) => {
  const label = playerLabel(player);
  if (player?.bot || player?.userId == null) {
    return (
      <>
        <Text>{label}</Text>
        {player?.bot && (
          <Tag style={{ marginLeft: 4 }} data-bot="1">
            bot
          </Tag>
        )}
      </>
    );
  }
  const userId = player.userId;
  return (
    <Button
      type="link"
      size="small"
      style={{ padding: 0, height: 'auto' }}
      data-player={userId}
      onClick={(e) => {
        e.stopPropagation();
        onOpenPlayer(userId);
      }}
    >
      {label}
    </Button>
  );
};

interface Props {
  rows: PvpMatchRow[];
  loading: boolean;
  onOpenMatch: (id: string) => void;
  onOpenPlayer: (userId: string) => void;
}

/** The match list: when (IST), players, result, reason, score, fee, payout, sim version, duration. */
export const MatchesTable: React.FC<Props> = ({ rows, loading, onOpenMatch, onOpenPlayer }) => {
  const columns: ColumnsType<PvpMatchRow> = [
    {
      title: 'When (IST)',
      key: 'when',
      render: (_, r) => (
        <span>
          {whenLabel(r.createdAt)}
          {r.status !== 'ended' && (
            <Tag color={STATUS_COLOR[r.status]} style={{ marginLeft: 6 }}>
              {r.status}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Players',
      key: 'players',
      render: (_, r) => (
        <span>
          <PlayerName player={r.players?.A} onOpenPlayer={onOpenPlayer} /> vs{' '}
          <PlayerName player={r.players?.B} onOpenPlayer={onOpenPlayer} />
        </span>
      ),
    },
    { title: 'Result', key: 'result', render: (_, r) => resultLabel(r) },
    { title: 'Reason', key: 'reason', render: (_, r) => reasonLabel(r.reason) },
    { title: 'Score', key: 'score', render: (_, r) => scoreLabel(r.innings) },
    { title: 'Fee', key: 'fee', align: 'right', render: (_, r) => fmtNum(r.entryFee, 0) },
    { title: 'Payout (A / B)', key: 'payout', render: (_, r) => payoutLabel(r.payout) },
    { title: 'Sim', key: 'sim', align: 'right', render: (_, r) => fmtNum(r.simVersion, 0) },
    { title: 'Duration', key: 'duration', render: (_, r) => fmtDuration(r.durationSec) },
  ];

  return (
    <Table<PvpMatchRow>
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={false}
      scroll={{ x: 'max-content' }}
      onRow={(r) => ({
        onClick: () => onOpenMatch(r.id),
        style: { cursor: 'pointer' },
        'data-match': r.id,
      })}
    />
  );
};
